import dayjs, { Dayjs } from "dayjs";
import {
  bookableHoursOf,
  onboardingLiveTimezone,
  toStartUtc,
  UserGroupAvailability,
  weekDayOf,
  weekDaysInOrder,
} from "../onboardingLiveTime.ts";
import { OnboardingLiveSlotPlanStatus } from "../../gql/graphql.ts";

export type SlotCell = { day: string; hour: number };

export type PlannedSlot = {
  slotId: string;
  status?: OnboardingLiveSlotPlanStatus;
};

export type SlotCellState =
  | { kind: "free" }
  | { kind: "selected" }
  | { kind: "repeated" }
  | { kind: "skipped" }
  | { kind: "planned"; slot: PlannedSlot }
  | { kind: "existingLive" }
  | { kind: "unavailable" }
  | { kind: "past" };

const dayFormat = "YYYY-MM-DD";

export function cellKey({ day, hour }: SlotCell): string {
  return `${day} ${hour}`;
}

export function cellOf(startUtc: string): SlotCell {
  const start = dayjs(startUtc).tz(onboardingLiveTimezone);
  return { day: start.format(dayFormat), hour: start.hour() };
}

export function startUtcOf({ day, hour }: SlotCell): string {
  return toStartUtc(day, hour);
}

export function todayInRome(now: Dayjs): string {
  return now.tz(onboardingLiveTimezone).format(dayFormat);
}

export function addDays(day: string, days: number): string {
  return dayjs.utc(day).add(days, "day").format(dayFormat);
}

export function mondayOf(day: string): string {
  const daysSinceMonday = (dayjs.utc(day).day() + 6) % 7;
  return addDays(day, -daysSinceMonday);
}

export function daysOfWeekStartingOn(monday: string): string[] {
  return weekDaysInOrder.map((_, index) => addDays(monday, index));
}

export function gridHoursOf(
  availability: UserGroupAvailability | null | undefined,
): number[] {
  return bookableHoursOf(
    availability,
    weekDaysInOrder.map((w) => w.weekDay),
  );
}

export function isBookable(
  { day, hour }: SlotCell,
  availability: UserGroupAvailability | null | undefined,
): boolean {
  return bookableHoursOf(availability, [weekDayOf(day)]).includes(hour);
}

export function isPast(cell: SlotCell, now: Dayjs): boolean {
  return !dayjs(startUtcOf(cell)).isAfter(now);
}

export function repeatedCells(cells: SlotCell[], weeks: number): SlotCell[] {
  const cellsByKey = new Map<string, SlotCell>();
  for (const cell of cells)
    for (let week = 0; week < weeks; week++) {
      const repeated = { day: addDays(cell.day, week * 7), hour: cell.hour };
      cellsByKey.set(cellKey(repeated), repeated);
    }
  return Array.from(cellsByKey.values());
}

export function stateOfCell({
  cell,
  now,
  availability,
  plannedByKey,
  existingLiveKeys,
  selectedKeys,
  repeatedKeys,
  skippedKeys,
}: {
  cell: SlotCell;
  now: Dayjs;
  availability: UserGroupAvailability | null | undefined;
  plannedByKey: Map<string, PlannedSlot>;
  existingLiveKeys: Set<string>;
  selectedKeys: Set<string>;
  repeatedKeys: Set<string>;
  skippedKeys: Set<string>;
}): SlotCellState {
  const key = cellKey(cell);
  const planned = plannedByKey.get(key);
  if (planned) return { kind: "planned", slot: planned };
  if (existingLiveKeys.has(key)) return { kind: "existingLive" };
  if (!isBookable(cell, availability)) return { kind: "unavailable" };
  if (isPast(cell, now)) return { kind: "past" };
  if (selectedKeys.has(key)) return { kind: "selected" };
  if (repeatedKeys.has(key))
    return skippedKeys.has(key) ? { kind: "skipped" } : { kind: "repeated" };
  return { kind: "free" };
}
